import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonButton,
  IonIcon,
  IonChip,
  IonLabel,
  IonCard,
  IonCardContent,
  IonBadge,
  IonSegment,
  IonSegmentButton,
  IonList,
  IonItem,
  IonTextarea
} from '@ionic/react';
import { useParams, useHistory } from 'react-router-dom';
import {
  checkmarkCircle,
  closeCircle,
  bulb,
  play,
  refresh,
  trophy,
  time,
  star
} from 'ionicons/icons';

import CodeEditor from '../components/CodeEditor';
import OutputPanel from '../components/OutputPanel';
import MascotMessage from '../components/MascotMessage';
import CelebrationEffect from '../components/CelebrationEffect';
import SEO from '../components/SEO';
import { configService } from '../services/ConfigService';
import { progressService } from '../services/ProgressService';
import { achievementService } from '../services/AchievementService';
import { seoService } from '../services/SEOService';
import { useCodeExecution } from '../hooks/useCodeExecution';
import { adInterstitial } from '../components/AdInterstitial';
import { soundService } from '../services/SoundService';
import { hapticService } from '../services/HapticService';

import './ChallengeDetail.css';

function ChallengeDetail() {
  const { id } = useParams();
  const history = useHistory();
  const config = configService.getConfig();
  const challenges = configService.getChallenges();
  
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [activeTab, setActiveTab] = useState('description');
  const [hintsUsed, setHintsUsed] = useState(0);
  const [testResults, setTestResults] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [startTime, setStartTime] = useState(null);
  
  const { execute, output, error, isRunning, executionTime, clearOutput } = useCodeExecution();

  useEffect(() => {
    if (challenges?.challenges) {
      const found = challenges.challenges.find(c => c.id === id);
      if (found) {
        setChallenge(found);
        setCode(found.starterCode || '');
        setStartTime(Date.now());
      }
    }
  }, [id, challenges]);

  // Helper function to check if test case passed
  const checkTestPassed = (actual, expected) => {
    const actualTrimmed = actual?.trim() || '';
    const expectedTrimmed = expected?.trim() || '';
    
    // Special case: "any" means any non-empty output passes
    if (expectedTrimmed.toLowerCase() === 'any') {
      return actualTrimmed.length > 0;
    }
    
    // Exact match
    return actualTrimmed === expectedTrimmed;
  };

  // Run code only (without test cases) - for CodeEditor's Run button
  const handleRunOnly = async (codeToRun) => {
    if (!challenge) return;
    await execute(codeToRun || code);
  };

  // Run code and test cases
  const handleRunCode = async () => {
    if (!challenge) return;
    
    setAttempts(prev => prev + 1);
    const result = await execute(code);
    
    // Run test cases
    if (challenge.testCases) {
      const results = [];
      for (const testCase of challenge.testCases) {
        const testResult = await execute(code, testCase.input);
        const passed = checkTestPassed(testResult.output, testCase.expectedOutput);
        results.push({
          ...testCase,
          actual: testResult.output?.trim(),
          passed,
          error: testResult.error
        });
      }
      setTestResults(results);
      
      // Check if all tests pass
      const allPassed = results.every(r => r.passed);
      if (allPassed && !isComplete) {
        handleChallengeComplete();
      }
    }
  };

  const handleChallengeComplete = async () => {
    soundService.playSuccess();
    hapticService.success();
    setIsComplete(true);
    setShowCelebration(true);
    
    const timeTaken = Math.round((Date.now() - startTime) / 1000);
    
    // Calculate points
    let points = config.gamification.pointsPerChallenge;
    if (hintsUsed === 0) points += 25; // No hints bonus
    if (attempts === 1) points += config.gamification.firstTryBonus; // First try bonus
    
    // Save progress
    await progressService.completeChallenge(id, {
      timeTaken,
      attempts,
      hintsUsed,
      points
    });
    
    // Check for badge unlocks
    achievementService.checkAndUnlockBadges();
    
    // Show interstitial ad (FREE version)
    if (adInterstitial?.showAfterChallenge) {
      await adInterstitial.showAfterChallenge();
    }
    
    // Hide celebration after delay
    setTimeout(() => {
      setShowCelebration(false);
    }, 3000);
  };

  const handleShowHint = () => {
    if (!challenge?.hints || hintsUsed >= challenge.hints.length) return;
    soundService.playClick();
    hapticService.light();
    
    // For FREE version, show rewarded ad for hints
    if (adInterstitial?.showRewarded) {
      adInterstitial.showRewarded(() => {
        setHintsUsed(prev => prev + 1);
      });
    } else {
      setHintsUsed(prev => prev + 1);
    }
  };

  const handleReset = () => {
    soundService.playClick();
    hapticService.light();
    if (challenge) {
      setCode(challenge.starterCode || '');
      setTestResults([]);
      setIsComplete(false);
    }
  };

  // Get SEO meta - dynamic per challenge
  const languageName = challenge ? (seoService.appConfig?.language?.charAt(0).toUpperCase() + seoService.appConfig?.language?.slice(1)) : 'Programming';
  const challengeMeta = challenge ? {
    title: `${challenge.title} - ${languageName} Challenge`,
    description: challenge.description || `Solve the ${challenge.title} coding challenge`,
    canonicalUrl: seoService.baseUrl + `/challenges/${challenge.id}`
  } : seoService.getPageMeta('challenges');

  if (!challenge) {
    return (
      <>
        <SEO
          title={challengeMeta.title}
          description={challengeMeta.description}
          canonicalUrl={challengeMeta.canonicalUrl}
          ogImage={seoService.getOgImage()}
        />
        <IonPage>
          <IonContent className="ion-padding">
            <div className="loading-container">
              <p>Loading challenge...</p>
            </div>
          </IonContent>
        </IonPage>
      </>
    );
  }

  const passedTests = testResults.filter(t => t.passed).length;
  const totalTests = testResults.length;

  return (
    <>
      <SEO
        title={challengeMeta.title}
        description={challengeMeta.description}
        canonicalUrl={challengeMeta.canonicalUrl}
        ogImage={seoService.getOgImage()}
      />
      <IonPage>
        <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/challenges" />
          </IonButtons>
          <IonTitle>{challenge.title}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleReset}>
              <IonIcon icon={refresh} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <CelebrationEffect 
          active={showCelebration} 
          type="confetti" 
          message="🎉 Challenge Complete!"
        />
        
        <div className="challenge-detail-container">
          {/* Challenge Header */}
          <div className="challenge-header">
            <div className="challenge-meta">
              <IonChip color={
                challenge.difficulty === 'easy' ? 'success' :
                challenge.difficulty === 'medium' ? 'warning' : 'danger'
              }>
                {challenge.difficulty}
              </IonChip>
              <IonChip>
                <IonIcon icon={trophy} />
                <IonLabel>{challenge.points} pts</IonLabel>
              </IonChip>
              {challenge.timeLimit && (
                <IonChip>
                  <IonIcon icon={time} />
                  <IonLabel>{challenge.timeLimit}</IonLabel>
                </IonChip>
              )}
            </div>
            
            {isComplete && (
              <IonBadge color="success" className="completed-badge">
                <IonIcon icon={checkmarkCircle} /> Completed!
              </IonBadge>
            )}
          </div>

          {/* Tabs */}
          <IonSegment value={activeTab} onIonChange={e => setActiveTab(e.detail.value)}>
            <IonSegmentButton value="description">
              <IonLabel>Challenge</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="hints">
              <IonLabel>Hints ({hintsUsed}/{challenge.hints?.length || 0})</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="tests">
              <IonLabel>Tests {totalTests > 0 ? `(${passedTests}/${totalTests})` : ''}</IonLabel>
            </IonSegmentButton>
          </IonSegment>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'description' && (
              <div className="description-tab">
                <h2>{challenge.title}</h2>
                <p className="challenge-description">{challenge.description}</p>
                
                {challenge.examples && (
                  <div className="examples-section">
                    <h3>Examples</h3>
                    {challenge.examples.map((example, idx) => (
                      <div key={idx} className="example-box">
                        <div className="example-input">
                          <strong>Input:</strong>
                          <pre>{example.input}</pre>
                        </div>
                        <div className="example-output">
                          <strong>Output:</strong>
                          <pre>{example.output}</pre>
                        </div>
                        {example.explanation && (
                          <div className="example-explanation">
                            <em>{example.explanation}</em>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {challenge.constraints && (
                  <div className="constraints-section">
                    <h3>Constraints</h3>
                    <ul>
                      {challenge.constraints.map((c, idx) => (
                        <li key={idx}>{c}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'hints' && (
              <div className="hints-tab">
                <MascotMessage
                  state="thinking"
                  message={hintsUsed > 0 ? "Here are your hints!" : "Need help? Unlock a hint!"}
                />
                
                {challenge.hints?.slice(0, hintsUsed).map((hint, idx) => (
                  <IonCard key={idx} className="hint-card">
                    <IonCardContent>
                      <div className="hint-header">
                        <IonIcon icon={bulb} color="warning" />
                        <span>Hint {idx + 1}</span>
                      </div>
                      <p>{hint}</p>
                    </IonCardContent>
                  </IonCard>
                ))}

                {hintsUsed < (challenge.hints?.length || 0) && (
                  <IonButton 
                    expand="block" 
                    fill="outline"
                    onClick={handleShowHint}
                  >
                    <IonIcon icon={bulb} slot="start" />
                    Unlock Hint {hintsUsed + 1}
                    {configService.isAdsEnabled() && ' (Watch Ad)'}
                  </IonButton>
                )}

                {hintsUsed >= (challenge.hints?.length || 0) && challenge.hints?.length > 0 && (
                  <p className="all-hints-used">All hints revealed!</p>
                )}
              </div>
            )}

            {activeTab === 'tests' && (
              <div className="tests-tab">
                {testResults.length === 0 ? (
                  <div className="no-tests">
                    <MascotMessage
                      state="explain"
                      message="Run your code to see test results!"
                    />
                  </div>
                ) : (
                  <IonList>
                    {testResults.map((result, idx) => (
                      <IonItem key={idx} className={`test-result ${result.passed ? 'passed' : 'failed'}`}>
                        <IonIcon 
                          icon={result.passed ? checkmarkCircle : closeCircle} 
                          color={result.passed ? 'success' : 'danger'}
                          slot="start"
                        />
                        <IonLabel>
                          <h3>Test Case {idx + 1} {result.hidden ? '(Hidden)' : ''}</h3>
                          {!result.hidden && (
                            <>
                              <p><strong>Input:</strong> {result.input || 'None'}</p>
                              <p><strong>Expected:</strong> {result.expectedOutput}</p>
                              <p><strong>Got:</strong> {result.actual || '(no output)'}</p>
                            </>
                          )}
                          {result.error && (
                            <p className="test-error"><strong>Error:</strong> {result.error}</p>
                          )}
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                )}

                {totalTests > 0 && (
                  <div className="test-summary">
                    <IonChip color={passedTests === totalTests ? 'success' : 'warning'}>
                      <IonIcon icon={passedTests === totalTests ? checkmarkCircle : star} />
                      <IonLabel>{passedTests}/{totalTests} tests passed</IonLabel>
                    </IonChip>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Code Editor Section */}
          <div className="editor-section">
            <h3>Your Solution</h3>
            <CodeEditor
              code={code}
              onChange={setCode}
              language={config.app.language}
              editable={true}
              runnable={true}
              onRun={handleRunOnly}
              running={isRunning}
            />
          </div>

          {/* Output Section */}
          <OutputPanel
            output={output}
            error={error}
            isRunning={isRunning}
            executionTime={executionTime}
          />

          {/* Action Buttons */}
          <div className="action-buttons">
            <IonButton 
              expand="block" 
              onClick={handleRunCode}
              disabled={isRunning || !code.trim()}
              color="primary"
            >
              <IonIcon icon={play} slot="start" />
              {isRunning ? 'Running...' : 'Run & Test'}
            </IonButton>

            {isComplete && (
              <IonButton 
                expand="block" 
                color="success"
                onClick={() => history.push('/challenges')}
              >
                <IonIcon icon={trophy} slot="start" />
                Back to Challenges
              </IonButton>
            )}
          </div>

          {/* Stats */}
          <div className="challenge-stats">
            <span>Attempts: {attempts}</span>
            <span>Hints: {hintsUsed}/{challenge.hints?.length || 0}</span>
          </div>
        </div>
      </IonContent>
    </IonPage>
    </>
  );
}

export default ChallengeDetail;